import type {
  AssistantCallCommand,
  AssistantCallMember,
  AssistantCallStart,
  AssistantCallStarted,
  AssistantCallStep,
  AssistantDirectory,
  AssistantRingAnswer,
  AssistantRingStatus,
  AssistantRingView,
} from '@crowdex/agent-contracts/api';
import { apiFetch, apiResponse } from '@crowdex/host-kit/renderer/http';

/** A step of an assistant turn, as the call shows it. */
export type TurnStep = AssistantCallStep;

/**
 * The user's assistants a call can reach (`/api/assistants`), home first: each with its name,
 * colour, avatar and channel. The voices come with the call.
 */
export async function loadAssistants(): Promise<AssistantCallMember[]> {
  const directory = await apiFetch<AssistantDirectory>('/api/assistants', { cache: 'no-store' });
  return directory.assistants
    .filter((assistant) => assistant.archivedAt === null)
    .map((assistant) => ({
      id: assistant.id,
      title: assistant.name,
      color: assistant.color,
      home: assistant.isHome === true,
      avatar: assistant.avatarId ?? null,
      conversation: assistant.conversationId,
    }));
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

/**
 * Starts the user's live call, which the server runs: the answer to the page's media offer, or
 * `call: null` when the ring it answers rang no more.
 */
export async function startCall(
  request: AssistantCallStart,
): Promise<AssistantCallStarted | { call: null; ring: AssistantRingAnswer }> {
  return await apiFetch('/api/assistant/calls', json(request));
}

/** The page's offer for the call's next session, which the server asked for. */
export async function offerSession(
  call: string,
  offer: { sdp: string },
): Promise<Record<string, unknown>> {
  return (
    await apiFetch<{ answer: Record<string, unknown> }>(
      `/api/assistant/calls/${encodeURIComponent(call)}/session`,
      json(offer),
    )
  ).answer;
}

/** What the user does in the call; hanging up still goes as the page closes. */
export function commandCall(call: string, command: AssistantCallCommand): Promise<void> {
  return apiResponse(`/api/assistant/calls/${encodeURIComponent(call)}`, {
    ...json(command),
    credentials: 'include',
    keepalive: command.type === 'hangup',
  }).then(() => undefined);
}

/** The assistant's ring going on now, if any. */
export async function loadRing(): Promise<AssistantRingView | null> {
  return (await apiFetch<AssistantRingStatus>('/api/assistant/ring', { cache: 'no-store' })).ring;
}

/** Declines the assistant's ring (answering it starts the call). */
export async function answerRing(ring: string, answer: boolean): Promise<AssistantRingAnswer> {
  return await apiFetch<AssistantRingAnswer>('/api/assistant/ring', json({ ring, answer }));
}
